import { useCallback, useRef, useState } from "react";
import { toast } from "react-toastify";

/* ---------------------------------------------------------------------------
   useRowActionFeedback — makes row CRUD actions feel interactive:
     - sets a per-row busy state while the request is in flight
     - flashes the row green (success) or red (failure) when it settles
     - shows a toast with your message

   const { busyId, flashId, flashKind, run } = useRowActionFeedback();

   await run({
     id: user.userId,
     action: () => fetch(...),
     success: "User updated",
     error: "Could not update user",
     flash: "success",
   });
--------------------------------------------------------------------------- */
export default function useRowActionFeedback() {
  const [busyId, setBusyId] = useState(null);
  const [flashId, setFlashId] = useState(null);
  const [flashKind, setFlashKind] = useState("success");
  const seq = useRef(0);

  const run = useCallback(
    async ({ id, action, success, error, flash = "success", refresh }) => {
      const ticket = ++seq.current;
      setBusyId(id);
      try {
        const out = await action();
        // Ignore stale completions if another action started meanwhile.
        if (ticket === seq.current) {
          setBusyId(null);
          setFlashKind(flash);
          setFlashId(id);
          setTimeout(() => setFlashId((v) => (v === id ? null : v)), 1500);
        }
        if (success) toast.success(success);
        if (refresh) await refresh();
        return out;
      } catch (err) {
        if (ticket === seq.current) setBusyId(null);
        const message =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          error ||
          "Something went wrong";
        if (error) toast.error(error);
        else toast.error(message);
        throw err;
      }
    },
    []
  );

  return { busyId, flashId, flashKind, run, setFlashId, setFlashKind };
}
